// frontend/src/components/owner/OwnerAssociationList.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { connectSocket, joinAssociation, onAssociationUpdate, onAssociationControl, onAutomationAction } from '../../services/socket';
import Card from '../common/Card';
import Loading from '../common/Loading';
import AssociationCard from '../admin/AssociationCard';
import AssociationDetails from '../admin/AssociationDetails';

const OwnerAssociationList = () => {
  const { user } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [filters, setFilters] = useState({
    active: 'all',
    autoMode: 'all',
    search: '',
  });

  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    try {
      const response = await api.get(`/associations/owner/${user.id}`);
      setAssociations(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ SOCKET.IO - Temps réel pour voir les changements des Simples
  useEffect(() => {
    if (associations.length === 0) return;

    const socket = connectSocket();
    if (!socket) return;

    // Rejoindre les rooms de toutes les associations de l'Owner
    associations.forEach(assoc => {
      joinAssociation(assoc._id);
    });

    // Écouter les mises à jour (autoMode changé par Simple)
    const cleanupUpdate = onAssociationUpdate((data) => {
      setAssociations(prev => prev.map(assoc => {
        if (assoc._id === data.associationId) {
          return {
            ...assoc,
            autoMode: data.autoMode !== undefined ? data.autoMode : assoc.autoMode,
          };
        }
        return assoc;
      }));
    });

    // Écouter les contrôles manuels (par Simple) — mise à jour ciblée, sans
    // refetch complet (AssociationCard reflète déjà l'état ON/OFF via son propre
    // abonnement Socket.io ; ici on ne met à jour que le verrouillage manuel).
    const cleanupControl = onAssociationControl((data) => {
      setAssociations(prev => prev.map(assoc => {
        if (assoc._id !== data.associationId) return assoc;
        return {
          ...assoc,
          manualOverride: data.manualOverride !== undefined
            ? { ...assoc.manualOverride, enabled: data.manualOverride, state: data.switchState }
            : assoc.manualOverride,
        };
      }));
    });

    // Écouter l'automation — idem, mise à jour ciblée du verrouillage (l'automation
    // lève toujours le verrouillage manuel avant d'agir).
    const cleanupAutomation = onAutomationAction((data) => {
      setAssociations(prev => prev.map(assoc => {
        if (assoc._id !== data.associationId) return assoc;
        return { ...assoc, manualOverride: { ...assoc.manualOverride, enabled: false } };
      }));
    });

    return () => {
      cleanupUpdate();
      cleanupControl();
      cleanupAutomation();
    };
  }, [associations.length]);

  const filteredAssociations = associations.filter((assoc) => {
    if (filters.active !== 'all' && assoc.active !== (filters.active === 'true')) {
      return false;
    }
    if (filters.autoMode !== 'all' && assoc.autoMode !== (filters.autoMode === 'true')) {
      return false;
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        assoc.name.toLowerCase().includes(searchLower) ||
        assoc.sensorId?.name.toLowerCase().includes(searchLower) ||
        assoc.switchId?.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) return <Loading />;

  if (selectedAssociation) {
    return (
      <AssociationDetails
        association={selectedAssociation}
        onClose={() => setSelectedAssociation(null)}
        onUpdate={fetchAssociations}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Mes Associations</h1>
        <p className="text-gray-600 mt-1">
          Gérez vos associations Sensor ↔ Switch
        </p>
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Nom, sensor, switch..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={filters.active}
              onChange={(e) => setFilters({ ...filters, active: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="all">Tous</option>
              <option value="true">Actif</option>
              <option value="false">Inactif</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mode Auto
            </label>
            <select
              value={filters.autoMode}
              onChange={(e) => setFilters({ ...filters, autoMode: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="all">Tous</option>
              <option value="true">Activé</option>
              <option value="false">Désactivé</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Liste */}
      {filteredAssociations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssociations.map((association) => (
            <AssociationCard
              key={association._id}
              association={association}
              onClick={setSelectedAssociation}
              onUpdate={fetchAssociations}
            />
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              Aucune association ne vous est assignée
            </p>
            <p className="text-sm text-gray-400">
              Contactez votre administrateur pour obtenir des associations
            </p>
          </div>
        </Card>
      )}

      <div className="mt-6 text-sm text-gray-600">
        Total: {filteredAssociations.length} association(s)
      </div>
    </div>
  );
};

export default OwnerAssociationList;